"use client";

import { amplitudeClientAdapter, getAmplitudeDeviceId } from "@/lib/analytics/adapters/amplitude.client";
import { facebookClientAdapter } from "@/lib/analytics/adapters/facebook.client";
import {
  captureFbclidFromLocation,
  getFbc,
  getOrCreateFbp,
} from "@/lib/analytics/cookies";
import { logAnalyticsDebug } from "@/lib/analytics/debug";
import { createEventId } from "@/lib/analytics/eventId";
import type {
  AnalyticsProperties,
  AnalyticsUserTraits,
  ClientAnalyticsAdapter,
  TrackRequestBody,
} from "@/lib/analytics/types";

const clientAdapters: ClientAnalyticsAdapter[] = [
  facebookClientAdapter,
  amplitudeClientAdapter,
];

let ready = false;
let currentUserId: string | null = null;
let currentEmail: string | null = null;
let utmProps: AnalyticsProperties = {};

export function setAnalyticsUtm(utm: AnalyticsProperties | null) {
  utmProps = utm ?? {};
}

export function initAnalyticsClients() {
  if (typeof window === "undefined") return;
  captureFbclidFromLocation();
  getOrCreateFbp();
  // Idempotent: adapters guard themselves. May run before Pixel script loads, then again onLoad.
  for (const adapter of clientAdapters) {
    try {
      adapter.init();
    } catch (err) {
      console.error(`[analytics:${adapter.name}] init failed`, err);
    }
  }
  ready = true;
}

export function identifyAnalytics(
  userId: string | null,
  traits?: AnalyticsUserTraits,
) {
  currentUserId = userId;
  if (traits?.email) currentEmail = traits.email;
  if (!ready) initAnalyticsClients();
  logAnalyticsDebug("client", "identify", {
    userId,
    traits: traits ?? null,
  });
  for (const adapter of clientAdapters) {
    try {
      adapter.identify(userId, traits);
    } catch (err) {
      console.error(`[analytics:${adapter.name}] identify failed`, err);
    }
  }
}

export function resetAnalytics() {
  currentUserId = null;
  currentEmail = null;
  logAnalyticsDebug("client", "reset", {});
  for (const adapter of clientAdapters) {
    try {
      adapter.reset();
    } catch (err) {
      console.error(`[analytics:${adapter.name}] reset failed`, err);
    }
  }
}

export function trackAnalytics(
  name: string,
  properties?: AnalyticsProperties,
  options?: { eventId?: string },
): string {
  if (typeof window === "undefined") return options?.eventId ?? "";
  if (!ready) initAnalyticsClients();

  const eventId = options?.eventId ?? createEventId();
  const merged: AnalyticsProperties = {
    ...utmProps,
    ...(properties ?? {}),
  };

  logAnalyticsDebug("client", "track", {
    eventId,
    name,
    properties: merged,
    userId: currentUserId,
    channels: ["facebook_pixel", "amplitude_browser", "server_proxy"],
  });

  for (const adapter of clientAdapters) {
    try {
      adapter.track(name, merged, eventId);
    } catch (err) {
      console.error(`[analytics:${adapter.name}] track failed`, err);
    }
  }

  void sendToServer({
    eventId,
    name,
    properties: merged,
    userId: currentUserId,
    deviceId: getAmplitudeDeviceId(),
    fbp: getOrCreateFbp(),
    fbc: getFbc() ?? captureFbclidFromLocation(),
    email: currentEmail,
    eventSourceUrl: window.location.href,
  });

  return eventId;
}

async function sendToServer(body: TrackRequestBody) {
  const json = JSON.stringify(body);
  try {
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      const blob = new Blob([json], { type: "application/json" });
      const queued = navigator.sendBeacon("/api/analytics/track", blob);
      if (queued) return;
    }

    const res = await fetch("/api/analytics/track", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: json,
      keepalive: true,
    });
    if (!res.ok) {
      /* istanbul ignore next -- guards a body that cannot be read; the beacon path is the default */
      const text = await res.text().catch(() => "");
      console.error("[analytics] server track failed", res.status, text);
    }
  } catch (err) {
    console.error("[analytics] server track request failed", err);
  }
}
